import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Share, Platform } from 'react-native';
import { Text, Card, ActivityIndicator, Modal, Divider } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import BaseContainer from '@/components/BaseContainer';
import Button from '@/components/Button';
import ScreenMargin from '@/components/ScreenMargin';
import { useToast } from 'react-native-toast-notifications';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/components/Auth/AuthContext';
import { Color as ThemeColors } from '@/GlobalStyles';
import { useForm } from 'react-hook-form';
import Form from '@/components/Form/Form';
import FTextInput from '@/components/Form/TextInput';
import IconListInput from '@/components/Form/IconListInput';
import { AvailableValidationRules } from '@/components/Form/validation';
import useFormStyle from '@/components/Styles/useFormStyle';
import PatientEmailService from '@/services/patientEmailService';
import {
  useGetPatientForLinkQuery,
  useGeneratePatientTokenWithExpiryMutation,
  useUpdatePatientEmailMutation,
} from '@/data/data';

interface FormData {
  email: string;
  customMessage: string;
  linkExpiry: string;
}

interface GeneratedTokenData {
  token: string;
  expiresAt: string;
}

export default function SendLinkScreen() {
  const { detail } = useLocalSearchParams<{ detail: string }>();
  const router = useRouter();
  const toast = useToast();
  const { t } = useTranslation();
  const { payload } = useAuthContext();
  const formStyles = useFormStyle();

  const { data: patientData, loading: patientLoading } = useGetPatientForLinkQuery({
    variables: { id: detail || '' },
    skip: !detail,
  });

  const patient = patientData?.patientByPk;

  const form = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      customMessage: t('sendLink.defaultMessage'),
      linkExpiry: '48',
    },
  });

  const { handleSubmit, setValue, watch } = form;

  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<GeneratedTokenData | null>(null);

  const [updatePatientEmail] = useUpdatePatientEmailMutation({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (patient) {
      if (patient.email) setValue('email', patient.email);
    }
  }, [patient, setValue]);

  const [generateToken] = useGeneratePatientTokenWithExpiryMutation({
    onCompleted: (data) => {
      if (data?.insertPatientTempTokensOne) {
        const tokenData = data.insertPatientTempTokensOne;
        setGeneratedToken(tokenData);
        const link = PatientEmailService.generatePatientFormUrl(tokenData.token);
        setGeneratedLink(link);
        setModalVisible(true);
        toast.show(t('sendLink.linkGeneratedSuccess'), { type: 'success' });
      }
    },
    onError: (error) => {
      console.error('Error generating token:', error);
      toast.show(t('sendLink.linkGenerationError'), { type: 'error' });
    },
    fetchPolicy: 'network-only',
  });

  const handleGenerateLink = handleSubmit(async (data) => {
    // Validation
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      toast.show(t('sendLink.emailRequired'), { type: 'warning' });
      return;
    }

    setGenerating(true);
    try {
      if (patient) {
        // Mettre à jour l'email du patient si nécessaire
        if (data.email !== patient?.email) {
          await updatePatientEmail({
            variables: {
              id: detail,
              email: data.email,
            },
          });
        }

        const expiryHours = parseInt(data.linkExpiry);
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + expiryHours);

        await generateToken({
          variables: {
            patientId: detail,
            expiresAt: expiresAt.toISOString(),
          },
        });
      }
    } catch (error) {
      console.error('Error generating link:', error);
      toast.show(t('sendLink.linkGenerationError'), { type: 'error' });
    } finally {
      setGenerating(false);
    }
  });

  const handleSendLink = async () => {
    if (!generatedToken) return;

    const formData = form.getValues();

    try {
      if (patient) {
        setGenerating(true);

        const surgeonName =
          `${payload?.given_name || patient?.chirurgien?.prenom || ''} ${payload?.family_name || patient?.chirurgien?.nom || ''}`.trim();

        // Remplacer le placeholder [LIEN_QUESTIONNAIRE] par le lien réel
        const messageWithLink = formData.customMessage.replace(
          '[LIEN_QUESTIONNAIRE]',
          generatedLink
        );

        await PatientEmailService.sendPatientFormEmail({
          to: formData.email,
          patientName: PatientEmailService.formatPatientName(patient.prenom, patient.nom),
          surgeonName: surgeonName || 'Votre chirurgien',
          tokenUrl: generatedLink,
          expiresAt: new Date(generatedToken.expiresAt),
          clinicName: payload?.['custom:clinic_name'],
          customMessage: messageWithLink, // Passer le message personnalisé
        });

        setModalVisible(false);
        toast.show(t('sendLink.emailSentSuccess'), { type: 'success' });

        // Redirection après succès
        setTimeout(() => {
          router.back();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      toast.show(error.message || t('sendLink.emailSendError'), { type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleShareLink = async () => {
    try {
      await Share.share({
        message: `Questionnaire IOL Match: ${generatedLink}`,
        url: generatedLink,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleCopyLink = () => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(generatedLink);
      toast.show(t('sendLink.linkCopied'), { type: 'success' });
    }
  };

  if (patientLoading) {
    return (
      <BaseContainer>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={ThemeColors.MainBleu} />
          <Text style={{ marginTop: 16 }}>{t('sendLink.loadingPatient')}</Text>
        </View>
      </BaseContainer>
    );
  }

  if (!patient) {
    return (
      <BaseContainer>
        <View style={styles.centered}>
          <Text>{t('sendLink.patientNotFound')}</Text>
          <Button mode="text" onPress={() => router.back()} style={{ marginTop: 16 }}>
            {t('sendLink.back')}
          </Button>
        </View>
      </BaseContainer>
    );
  }

  return (
    <>
      <BaseContainer withScrollView={'keyboardAware'}>
        <ScreenMargin>
          <Text style={formStyles?.headerForm}>{t('sendLink.title')}</Text>
          <Text variant="bodyMedium" style={{ marginBottom: 16 }}>
            {t('sendLink.patientLabel')}: {patient.prenom} {patient.nom}
          </Text>

          <Form<FormData> {...form}>
            <View style={formStyles?.bodyForm}>
              <View style={formStyles?.groupForm}>
                <Text style={formStyles?.titleGroupForm}>{t('sendLink.configuration')}</Text>

                <View style={formStyles.rowForm}>
                  <FTextInput
                    name="email"
                    label={t('sendLink.patientEmail')}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    validators={[AvailableValidationRules.required, AvailableValidationRules.email]}
                    withMargin={false}
                    containerStyle={formStyles.input2Col}
                  />

                  <IconListInput
                    name="linkExpiry"
                    label={t('sendLink.linkValidity')}
                    data={[
                      { value: '24', label: t('sendLink.validity24h'), icon: 'clock-fast' },
                      { value: '48', label: t('sendLink.validity48h'), icon: 'clock' },
                      { value: '72', label: t('sendLink.validity72h'), icon: 'clock-end' },
                    ]}
                    withMargin={false}
                    containerStyle={formStyles.input2Col}
                  />
                </View>
              </View>

              <View style={formStyles?.groupForm}>
                <Text style={formStyles?.titleGroupForm}>{t('sendLink.customMessage')}</Text>
                <Text
                  style={{ fontSize: 12, color: '#666', marginBottom: 8, paddingHorizontal: 7 }}
                >
                  {t('sendLink.customMessageHelp')}
                </Text>
                <FTextInput
                  name="customMessage"
                  label=""
                  multiline
                  placeholder={t('sendLink.customMessagePlaceholder')}
                  withMargin={false}
                  containerStyle={formStyles.input1Col}
                  contentStyle={{
                    height: 200,
                  }}
                  style={{
                    textAlignVertical: 'top',
                    paddingTop: 12,
                    fontSize: 14,
                  }}
                />
              </View>
            </View>

            <View style={formStyles?.footerForm}>
              <Button
                mode="contained"
                onPress={handleGenerateLink}
                loading={generating}
                icon="link-variant"
                style={styles.button}
              >
                {t('sendLink.generateLink')}
              </Button>
            </View>
          </Form>
        </ScreenMargin>
      </BaseContainer>

      <Modal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        contentContainerStyle={styles.modal}
      >
        <Text variant="headlineSmall" style={{ marginBottom: 16 }}>
          {t('sendLink.linkGeneratedTitle')}
        </Text>

        <Text>{t('sendLink.linkGeneratedDesc')}</Text>
        <Card style={styles.linkCard}>
          <Text style={styles.linkText}>{generatedLink}</Text>
        </Card>

        <View style={{ marginTop: 16 }}>
          <Text variant="bodyMedium" style={{ marginBottom: 8 }}>
            {t('sendLink.patientLabel')} : {patient?.prenom} {patient?.nom}
          </Text>
          <Text variant="bodySmall">
            {t('sendLink.validityLabel', { hours: watch('linkExpiry') })}
          </Text>
        </View>

        <Divider style={{ marginVertical: 16 }} />

        <View style={styles.modalActions}>
          <Button mode="text" onPress={handleCopyLink} icon="content-copy">
            {t('sendLink.copy')}
          </Button>
          <Button mode="text" onPress={handleShareLink} icon="share-variant">
            {t('sendLink.share')}
          </Button>
          <Button mode="contained" onPress={handleSendLink}>
            {t('sendLink.send')}
          </Button>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    marginTop: 16,
    marginBottom: 32,
  },
  modal: {
    backgroundColor: 'white',
    padding: 20,
    margin: 20,
    borderRadius: 8,
  },
  linkCard: {
    marginTop: 12,
    padding: 8,
    backgroundColor: '#f5f5f5',
  },
  linkText: {
    fontSize: 12,
    fontFamily: Platform.select({
      web: 'monospace',
      default: 'Courier',
    }),
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
});
